import { useContext, useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Box, Grid, IconButton } from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CircularIndicator from "../elements/CircularIndicator";
import Typography from "@mui/material/Typography";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import VerticalProgressBar from "../elements/VerticalProgressBar";
import TrackerForm from "../elements/TrackerForm";
import DailyTasks from "../elements/DailyTasks";
import { DataContext } from "../../context/data-provider";
import axiosInstance from "../../services/axios-instance";
import {
    HOLIDAYS_REQUESTS_URL,
    WEEKLY_LOGS_URL,
    GET_HOLIDAYS_DATELIST,
    NATIONAL_HOLIDAYS_URL,
    HOME_OFFICE_DAYS,
    HOME_OFFICE_DAYS_BY_ID
} from "../../services/endpoints";
import { CurrentWeekLogsInterface } from "../../interface/currentWeekLogsInterface";
import { useOutletContext } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import dayjs from "dayjs";
import { getHolidaysInCurrentMonth } from "../utils/getHolidaysInCurrentMonth";
import { MonthDataParams } from "../../interface/monthDataInterface";
import { calculateHoursPerDay } from "../utils/calculateHoursPerDay";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { NationalHolidaysInterface } from "../../interface/nationalHolidayInterface";
import HomeWorkIcon from '@mui/icons-material/HomeWork';

const FirstGrid = styled(Box)(({ theme }) => ({
    gridColumn: "span 8",
    background: `linear-gradient(to right, ${theme.palette.common.white}, ${theme.palette.grey[300]})`,
    padding: theme.spacing(2),
    borderRadius: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: 100,
}));

const SecondGrid = styled(Box)(({ theme }) => ({
    gridColumn: "span 4",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    height: 100,
    borderRadius: "8px",
}));

const Title = styled(Box)(({ theme }) => ({
    fontSize: theme.typography.h4.fontSize,
    fontWeight: "bold",
    color: theme.palette.primary.main,
}));

const Subtitle = styled(Box)(({ theme }) => ({
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.grey[600],
    fontWeight: "bold",
}));

const List = styled("ul")({
    listStyle: "none",
    padding: 0,
    margin: 0,
});

const ListItem = styled("li")(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.primary.main,
    borderRadius: "4px",
    padding: 10,
    margin: "0.5rem",
    fontSize: "12px",
}));

type SelectedWeekType = number;
type FirstDayOfWeekType = Date;

const Dashboard = () => {
    const [selectedItemIndices, setSelectedItemIndices] = useState<number[]>(
        []
    );
    const [weeklyHolidays, setWeeklyHolidays] = useState<string[]>([]);

    const [homeOfficeDaysCount, setHomeOfficeDaysCount] = useState<number>(0);

    const [homeOfficeStatus, setHomeOfficeStatus] = useState<string[]>([]);

    const [showWeekendLog, setShowWeekendLog] = useState(false);

    const theme = useTheme();
    
    const [editTaskDetails, setEditTaskDetails] =
        useState<CurrentWeekLogsInterface | null>(null);

    const toggleSelectedItem = (index: number) => {
        if (selectedItemIndices.includes(index)) {
            setSelectedItemIndices(
                selectedItemIndices.filter((i) => i !== index)
            );
        } else {
            setSelectedItemIndices([...selectedItemIndices, index]);
        }
    };

    const [showInputForm, setShowInputForm] = useState(false);
    const [selectedWeek, firstDayOfWeek] = useOutletContext() as [
        SelectedWeekType,
        FirstDayOfWeekType
    ];

    const [nationalHolidays, setNationalHolidays] = useState<
        NationalHolidaysInterface[]
    >([]);

    const daysOfWeek = [];
    const weekend = [];

    // Get the first day of the current week (Monday)
    const startOfTheWeek = new Date(firstDayOfWeek);

    // Add the dates of each day in the current week (Monday to Friday)
    for (let i = 0; i < 7; i++) {
        const date = new Date(startOfTheWeek);
        date.setDate(startOfTheWeek.getDate() + i);
        const weekdayNames = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
        ];

        const weekdayName = weekdayNames[date.getDay()];
        const weekDayDate = dayjs(date).format("YYYY-MM-DD");
        const weekDayTasks = {
            weekdayName,
            weekDayDate,
        };

        if (i < 5) {
            daysOfWeek.push(weekDayTasks);
        }
        else {
            weekend.push(weekDayTasks);
        }
    }


    const isHomeOfficeDay = (weekDayDate : any) => {
        return homeOfficeStatus.some(record => record === weekDayDate);
    };

    const toggleHomeOffice = (weekDayDate : any) => {
        if(isHomeOfficeDay(weekDayDate)) {
            removeHomeOffice(weekDayDate);
            setHomeOfficeDaysCount(homeOfficeDaysCount - 1);
            setHomeOfficeStatus(homeOfficeStatus.filter(date => date !== weekDayDate));
        }
        else {
            addHomeOffice(weekDayDate);
            setHomeOfficeDaysCount(homeOfficeDaysCount + 1);
            setHomeOfficeStatus([
                ...homeOfficeStatus,
                weekDayDate
            ]);
        }
    };

    const addHomeOffice = async (weekDayDate : any) => {
        try {
            const resHomeOffice = await axiosInstance.post(
                HOME_OFFICE_DAYS,
                {
                    user_id: currentUser.id,
                    dates: [weekDayDate],
                }
            );
            if (resHomeOffice.status === 200) {
                console.log("ok");
            }
        } catch (error) {
            console.error("An error occurred:", error);
        }
    };

    const removeHomeOffice = async (weekDayDate : any) => {

        try {
            const resHomeOffice = await axiosInstance.delete(HOME_OFFICE_DAYS_BY_ID, {
                data: 
                {
                    user_id: currentUser.id,
                    date: weekDayDate,
                }
            });
            if (resHomeOffice.status === 200) {
                console.log("ok");
            }
        } catch (error) {
            console.error("An error occurred:", error);
        }

    };

    const newCurrentDate = new Date().toLocaleDateString();

    const { currentUser } = useContext(DataContext);

    const selectedYear = new Date(firstDayOfWeek).getFullYear();

    const [currentWeekLogs, setCurrentWeekLogs] = useState<
        CurrentWeekLogsInterface[]
    >([]);

    const [allListOfHolidays, setAllListOfHolidays] = useState<
        MonthDataParams[]
    >([]);

    useEffect(() => {
        const fetchWeeklyLogs = async () => {
            try {
                if (currentUser.id) {
                    const response = await axiosInstance.get(
                        WEEKLY_LOGS_URL(
                            currentUser.id,
                            firstDayOfWeek.toString()
                        )
                    );
                    const responseHolidaysRequests = await axiosInstance.get(
                        HOLIDAYS_REQUESTS_URL(currentUser.id!)
                    );
                    setAllListOfHolidays(responseHolidaysRequests.data.data);
                    setCurrentWeekLogs(response.data.data);
                }
            } catch (error) {
                console.error(error);
            }
        };

        const fetchWeeklyHoliday = async () => {
            try {
                if (currentUser.id) {
                    const response = await axiosInstance.get(
                        GET_HOLIDAYS_DATELIST(
                            currentUser.id,
                            firstDayOfWeek.toString()
                        )
                    );
                    setWeeklyHolidays(response.data);
                }
            } catch (error) {
                console.error(error);
            }
        };

        const fetchHomeOfficeDays = async () => {
            try {
                if (currentUser.id) {
                    const response = await axiosInstance.get(HOME_OFFICE_DAYS, {
                        params: {
                            user_id: currentUser.id,
                            month: new Date().getMonth() + 1,
                        }
                    });
                    setHomeOfficeDaysCount(response.data.length);
                }
            } catch (error) {
                console.error(error);
            }
        };

        const fetchHomeOfficeStatus = async () => {
            try {
                const response = await axiosInstance.get(HOME_OFFICE_DAYS, {
                    params: {
                        user_id: currentUser.id,
                        from_date: startOfTheWeek,
                    }
                });
                setHomeOfficeStatus(response.data.map((item : any) => item.date));
            } catch (error) {
                console.error('Error fetching home office status:', error);
            }
        };

        if (!isNaN(selectedWeek) && !isNaN(selectedYear)) {
            fetchWeeklyLogs();
            fetchWeeklyHoliday();
            fetchHomeOfficeDays();
            fetchHomeOfficeStatus();
        }
    }, [currentUser.id, firstDayOfWeek]);

    useEffect(() => {
        const fetchHomeOfficeStatus = async () => {
            try {
                const response = await axiosInstance.get(HOME_OFFICE_DAYS, {
                    params: {
                        user_id: currentUser.id,
                        from_date: startOfTheWeek,
                    }
                });
                setHomeOfficeStatus(response.data.map((item : any) => item.date));
            } catch (error) {
                console.error('Error fetching home office status:', error);
            }
        };

        fetchHomeOfficeStatus();
    }, []);

    useEffect(() => {
        const fetchNationalHolidays = async () => {
            try {
                const responseNatinalHolidays = await axiosInstance.get(
                    NATIONAL_HOLIDAYS_URL
                );
                setNationalHolidays(responseNatinalHolidays.data.data);
            } catch (error) {
                console.error(error);
            }
        };

        fetchNationalHolidays();
    }, []);

    const [openedDateTrackerForm, setOpenedDateTrackerForm] =
        useState<string>("");

    const handleCloseModal = () => {
        setEditTaskDetails(null);
        setShowInputForm(false);
    };

    return (
        <>
            <Grid container spacing={2} style={{ display: "flex" }}>
                <Grid item xs={12} md={8}>
                    <FirstGrid className="welcome_banner">
                        <div>
                            <Title>Hello {currentUser.name}</Title>
                            <Subtitle>Welcome to your dashboard</Subtitle>
                        </div>
                        <Box
                            sx={{
                                fontWeight: "bold",
                                color: "primary.main",
                            }}
                        >
                            {newCurrentDate}
                        </Box>
                    </FirstGrid>
                </Grid>
                <Grid item xs={12} md={4}>
                    <SecondGrid
                        sx={{
                            backgroundColor: `${
                                calculateHoursPerDay(
                                    currentWeekLogs,
                                    selectedWeek,
                                    selectedYear,
                                    nationalHolidays,
                                    weeklyHolidays,
                                ).color
                            }`,
                        }}
                    >
                        <Box
                            sx={{
                                mr: 2,
                                borderRadius: "100%",
                                padding: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: `${
                                    calculateHoursPerDay(
                                        currentWeekLogs,
                                        selectedWeek,
                                        selectedYear,
                                        nationalHolidays,
                                        weeklyHolidays,
                                    ).circleColor
                                }`,
                            }}
                        >
                            <ThumbUpIcon
                                sx={{
                                    borderRadius: "100%",
                                    color: `${
                                        calculateHoursPerDay(
                                            currentWeekLogs,
                                            selectedWeek,
                                            selectedYear,
                                            nationalHolidays,
                                            weeklyHolidays,
                                        ).textColor
                                    }`,
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                color: `${
                                    calculateHoursPerDay(
                                        currentWeekLogs,
                                        selectedWeek,
                                        selectedYear,
                                        nationalHolidays,
                                        weeklyHolidays,
                                    ).textColor
                                }`,
                                fontWeight: "bold",
                            }}
                        >
                            {
                                calculateHoursPerDay(
                                    currentWeekLogs,
                                    selectedWeek,
                                    selectedYear,
                                    nationalHolidays,
                                    weeklyHolidays,
                                ).message
                            }
                        </Box>
                    </SecondGrid>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box>
                        <>
                            <List>
                                {daysOfWeek.map((day, i) => (
                                    <ListItem
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            cursor: "pointer",
                                        }}
                                        key={i}
                                    >
                                        <DailyTasks
                                            toggleHomeOffice={toggleHomeOffice}
                                            isHomeOfficeDay={isHomeOfficeDay}
                                            firstDayOfWeek={firstDayOfWeek}
                                            weekdayName={day.weekdayName}
                                            weekDayDate={day.weekDayDate}
                                            currentWeekLogs={currentWeekLogs}
                                            selectedItemIndices={
                                                selectedItemIndices
                                            }
                                            i={i}
                                            showInputForm={showInputForm}
                                            setShowInputForm={setShowInputForm}
                                            setOpenedDateTrackerForm={
                                                setOpenedDateTrackerForm
                                            }
                                            setCurrentWeekLogs={
                                                setCurrentWeekLogs
                                            }
                                            selectedWeek={selectedWeek}
                                            selectedYear={selectedYear}
                                            setEditTaskDetails={
                                                setEditTaskDetails
                                            }
                                        />
                                        <IconButton
                                            sx={{
                                                width: "36px",
                                                height: "36px",
                                            }}
                                            onClick={() =>
                                                toggleSelectedItem(i)
                                            }
                                        >
                                            {selectedItemIndices.includes(i) ? (
                                                <KeyboardArrowUpIcon />
                                            ) : (
                                                <KeyboardArrowDownIcon />
                                            )}
                                        </IconButton>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                        <Typography
                                className={`weekend-log ${showWeekendLog ? 'open' : 'closed'}`}
                                onClick={()=>{setShowWeekendLog(!showWeekendLog)}}
                                variant="body1"
                                fontWeight="bold"
                                mb="4px"
                                color="primary.main"
                                marginTop="2rem"
                            >
                                Weekend <ExpandMoreIcon/>
                            </Typography>

                        {showWeekendLog ? (
                        <>
                       <List>
                                {weekend.map((day, i) => (
                                    <ListItem
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                        }}
                                        key={i+5}
                                    >
                                        <DailyTasks
                                            toggleHomeOffice={toggleHomeOffice}
                                            isHomeOfficeDay={isHomeOfficeDay}
                                            firstDayOfWeek={firstDayOfWeek}
                                            weekdayName={day.weekdayName}
                                            weekDayDate={day.weekDayDate}
                                            currentWeekLogs={currentWeekLogs}
                                            selectedItemIndices={
                                                selectedItemIndices
                                            }
                                            i={i+5}
                                            showInputForm={showInputForm}
                                            setShowInputForm={setShowInputForm}
                                            setOpenedDateTrackerForm={
                                                setOpenedDateTrackerForm
                                            }
                                            setCurrentWeekLogs={
                                                setCurrentWeekLogs
                                            }
                                            selectedWeek={selectedWeek}
                                            selectedYear={selectedYear}
                                            setEditTaskDetails={
                                                setEditTaskDetails
                                            }
                                        />
                                        <IconButton
                                            sx={{
                                                width: "36px",
                                                height: "36px",
                                            }}
                                            onClick={() =>
                                                toggleSelectedItem(i+5)
                                            }
                                        >
                                            {selectedItemIndices.includes(i+5) ? (
                                                <KeyboardArrowUpIcon />
                                            ) : (
                                                <KeyboardArrowDownIcon />
                                            )}
                                        </IconButton>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                        ) : "" }
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Grid item xs={12} md={12} mt="12px">
                        <Box
                            sx={{
                                fontWeight: "bold",
                            }}
                        >
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                mb="4px"
                                color="primary.main"
                            >
                                TRACKING
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                {daysOfWeek.map((day, i) => (
                                    <Grid item key={i}>
                                        <VerticalProgressBar
                                            nationalHolidays={nationalHolidays}
                                            isWeekend={false}
                                            weekdayName={day.weekdayName}
                                            weekDayDate={day.weekDayDate}
                                            currentWeekLogs={currentWeekLogs}
                                            holidays={weeklyHolidays}
                                        />
                                    </Grid>
                                ))}

                                {weekend.map((day, i) => (
                                    <Grid item key={i+5}>
                                        <VerticalProgressBar
                                            nationalHolidays={nationalHolidays}
                                            isWeekend={true}
                                            weekdayName={day.weekdayName}
                                            weekDayDate={day.weekDayDate}
                                            currentWeekLogs={currentWeekLogs}
                                            holidays={weeklyHolidays}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12} mt="12px">
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            textTransform="uppercase"
                            mb="4px"
                            color="primary.main"
                        >
                            Clients
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                marginTop: "8px",
                                marginBottom: "8px",
                            }}
                        >
                            <ul className="clients_list">
                            {currentUser.clients && currentUser.clients.length > 0 ? (
                                 currentUser.clients.map((client) => (
                                <li key={client.id}>
                                    <span>{client.name}</span>
                                    <span>{(client?.pivot?.hours != null) ? `${client.pivot.hours}h` : ''}</span>
                                </li>
                            ))
                        ) : (
                            <li>No clients assigned. 
                                <br></br>
                                Please contact the administrator.
                            </li>
                        )}                       
                            </ul>
                        </Box>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            textTransform="uppercase"
                            mb="4px"
                            color="primary.main"
                        >
                            Monthly Summary
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                marginTop: "8px",
                                marginBottom: "8px",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    bgcolor: "holiday.iconBg",
                                    borderRadius: "100%",
                                    width: 40,
                                    height: 40,
                                }}
                            >
                                <BeachAccessIcon
                                    sx={{
                                        color: "holiday.icon",
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginLeft: "12px",
                                    marginRight: "10px",
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        marginRight: "8px",
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "primary.main",
                                    }}
                                >
                                    {getHolidaysInCurrentMonth(
                                        allListOfHolidays
                                    )}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        color: "primary.main",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <small
                                        style={{
                                            fontWeight: "bold",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Holidays
                                    </small>
                                    <small
                                        style={{
                                            marginTop: "-4px",
                                            fontSize: "10px",
                                            color: "gray",
                                        }}
                                    >
                                        for the current month
                                    </small>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    bgcolor: "homeOffice.iconBg",
                                    borderRadius: "100%",
                                    width: 40,
                                    height: 40,
                                }}
                            >
                                <HomeWorkIcon
                                    sx={{
                                        color: "homeOffice.icon",
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginLeft: "12px",
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        marginRight: "8px",
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "primary.main",
                                    }}
                                >
                                    {homeOfficeDaysCount}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        color: "primary.main",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <small
                                        style={{
                                            fontWeight: "bold",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Home Office Days
                                    </small>
                                    <small
                                        style={{
                                            marginTop: "-4px",
                                            fontSize: "10px",
                                            color: "gray",
                                        }}
                                    >
                                        for the current month
                                    </small>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                marginTop: "24px",
                            }}
                        >
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                color="primary.main"
                                textTransform="uppercase"
                            >
                                Holidays summary
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-end",
                                    marginTop: "8px",
                                }}
                            >
                                <p
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: "24px",
                                        color: "green",
                                    }}
                                >
                                    {currentUser?.available_days_left_all}
                                </p>
                                <small
                                    style={{
                                        marginLeft: "8px",
                                        marginBottom: "4px",
                                        fontWeight: "bold",
                                        color: "gray",
                                    }}
                                >
                                    days available
                                </small>
                            </Box>

                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                marginTop: "12px",
                            }}
                        >
                            <CircularIndicator
                                color={theme.palette.holiday.circularProgress}
                                holidaysUsed={
                                    currentUser?.available_days_left_current
                                }
                                holidaysLeft={
                                    currentUser?.available_days_current
                                }
                                year={new Date().getFullYear()}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
            {showInputForm && (
                <TrackerForm
                    openedDateTrackerForm={openedDateTrackerForm}
                    onClose={handleCloseModal}
                    setCurrentWeekLogs={setCurrentWeekLogs}
                    selectedWeek={selectedWeek}
                    selectedYear={selectedYear}
                    firstDayOfWeek={firstDayOfWeek}
                    editTaskDetails={editTaskDetails}
                />
            )}
        </>
    );
};

export default Dashboard;
